import React, { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { bool, shape, object } from 'prop-types';
import classNames from 'classnames';

import { flatSchema as addressSchema } from 'components/Address/util';
import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import {
  TextField,
  SelectField,
  Option,
  FieldSubText,
} from '@johnlewispartnership/wtr-ingredients/ingredients/Forms';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import TitleSelect from 'components/TitleSelect';
import { scrollIntoViewAndFocusElement } from 'utils/scroll';

import { firstNameValidator, lastNameValidator, phoneNumberValidator } from './validator';
import options from './country-telephone-code';

import styles from './AddressName.scss';

const AddressName = ({
  initialValues,
  formContext,
  extraValidations = null,
  isRegistering = false,
}) => {
  const { register, errors: validationErrors, setValue, formState } = formContext;
  const titleRef = useRef(null);
  const { title, firstName, lastName, contactNumber, countryDialingCode } = initialValues;
  let firstNameValidation = firstNameValidator;
  let lastNameValidation = lastNameValidator;
  const formStateTitleError = formState?.errors.title;
  const [currentDialingCode, setCurrentDialingCode] = useState(countryDialingCode);

  /**
   * This useEffect is used when the editMode is changed, because
   * when changing to edit mode the register was not being triggered and setting the default value
   * so with this we can add the value to the corresponding inputs.
   */
  useEffect(() => {
    setValue('firstName', firstName);
    setValue('lastName', lastName);
    setValue('contactNumber', contactNumber);
    if (isRegistering) {
      setValue('countryDialingCode', countryDialingCode);
    }
  }, [countryDialingCode, isRegistering, contactNumber, firstName, lastName, setValue]);

  useEffect(() => {
    if (formStateTitleError) {
      scrollIntoViewAndFocusElement(titleRef.current, {}, 0);
    }
  }, [formStateTitleError]);

  if (extraValidations) {
    const { firstNameExtraValidation, lastNameExtraValidation } = extraValidations;

    firstNameValidation = {
      ...firstNameValidation,
      ...firstNameExtraValidation,
      validate: { ...firstNameValidation.validate, ...firstNameExtraValidation?.validate },
    };
    lastNameValidation = {
      ...lastNameValidation,
      ...lastNameExtraValidation,
      validate: { ...lastNameValidation.validate, ...lastNameExtraValidation?.validate },
    };
  }

  return (
    <div data-testid="addressee-form">
      <div className={styles.titleSelect}>
        <FormProvider {...formContext}>
          <TitleSelect ref={titleRef} initialTitle={title} />
        </FormProvider>
      </div>
      <TextField
        autoComplete="given-name"
        defaultValue={firstName}
        errorText={validationErrors?.firstName?.message}
        id="firstName"
        label="First name"
        name="firstName"
        ref={register(firstNameValidation)}
        required
      />
      <TextField
        autoComplete="family-name"
        defaultValue={lastName}
        errorText={validationErrors?.lastName?.message}
        id="lastName"
        label="Last name"
        name="lastName"
        ref={register(lastNameValidation)}
        required
      />
      {isRegistering ? (
        <div>
          <div className={classNames(styles.contactNumber, 'row')}>
            <Typography element="span" noMargins styleAs="paragraphHeading" textStyle="heading-l">
              Contact number
            </Typography>
            <FieldSubText
              id="telephoneNumber-helpText"
              text="We use this if we need to contact you about your order"
              type="help"
            />
            <div
              data-testid="countryDialingCode"
              className={classNames(styles.firstColumn, 'col-xs-6of12')}
            >
              <SelectField
                autoComplete="tel-country-code"
                className={styles.selectOverflow}
                id="countryDialingCode"
                label="Country dialing code"
                name="countryDialingCode"
                onChange={event => {
                  const { value } = event.target;
                  setCurrentDialingCode(value);
                }}
                ref={register({ required: 'Please select a value' })}
              >
                {options.map(({ label, value }) => (
                  <Option
                    selected={countryDialingCode && countryDialingCode === value}
                    key={value}
                    value={value}
                  >
                    {label}
                  </Option>
                ))}
              </SelectField>
            </div>
            <div className={classNames(styles.secondColumn, 'col-xs-6of12')}>
              <TextField
                autoComplete="tel-national"
                defaultValue={contactNumber}
                errorText={validationErrors?.contactNumber?.message}
                id="telephoneNumber"
                label="Contact number"
                labelHidden
                name="contactNumber"
                ref={register(phoneNumberValidator)}
                required
                type="tel"
                inputMode="tel"
              />
            </div>
          </div>
          {currentDialingCode !== '+44' && (
            <div className={styles.internationalTelephoneAlert}>
              <Alert
                type="warning"
                title="International contact number"
                message="We may not be able to contact you unless you provide a UK mobile number"
              />
            </div>
          )}
        </div>
      ) : (
        <TextField
          defaultValue={contactNumber}
          autoComplete="tel"
          errorText={validationErrors?.contactNumber?.message}
          helpText="We use this if we need to contact you about your order"
          id="telephoneNumber"
          label="Contact number"
          name="contactNumber"
          ref={register(phoneNumberValidator)}
          required
          type="tel"
          inputMode="tel"
        />
      )}
    </div>
  );
};

AddressName.propTypes = {
  formContext: object.isRequired,
  isRegistering: bool,
  initialValues: shape(addressSchema).isRequired,
  editName: bool,
  extraValidations: object,
};

export default AddressName;
